import React, { useMemo, useContext, ReactNode } from 'react';
import { HoverEvents } from '@react-types/shared';

import { DraggableContext, HeaderRowContext } from '../context';
import { cn } from '../../../lib/css/className/className';
import './Row.css';
import { useColorStatus, StatusColorProps } from '../../../hooks/useColorStatus';
import { TestProps } from '../../../internal/types/testTypes';
import { ICON_SIZE } from '../constants';
import { TableRow, TableCell } from '../../Table';
import { Cell } from '../../Cell';
import { EmptyCell } from '../../EmptyCell';

import { MiniGridRowStatusIcon } from './StatusIcon/StatusIcon';

const cls = cn('MiniGridRow');
const clickableAria = {
    width: `${ICON_SIZE}px`,
    height: `${ICON_SIZE}px`,
};

export interface ChildrenProps {
    /**
     * Дочерние элементы
     * @expectedNode `Cell` | `TextCell` | `HeaderCell` | `EmptyCell`
     */
    children: React.ReactNode;
}

export interface MiniGridRowBaseProps extends TestProps, ChildrenProps, HoverEvents {
    /**
     * Названия классов для элемента
     */
    className?: string;

    /**
     * Атрибут id для элемента
     */
    id: string;

    /**
     * Отклчение верхнего бордера
     * border-top-color: transparent
     */
    withoutTopBorder?: boolean;

    /**
     * Название цвета статуса
     */
    statusColor?: StatusColorProps;

    /**
     * Обработчик нажатия на строку
     */
    onClick?: () => void;
    /**
     * Отображает справочную информацию при наведении на строку
     */
    title?: string;
    /**
     * Признак выделения строки
     */
    selected?: boolean;
    /**
     * Флаг наличия ошибки в строке (используется для подскролла к заданной строке)
     */
    'data-form-error'?: boolean;
    /**
     * Включение/Отключение перетаскивания
     * @default true
     */
    isDraggable?: boolean;
}

export type MiniGridRowWithoutStatusProps = MiniGridRowBaseProps & { error?: never; warning?: never };
export type MiniGridRowWithErrorProps = MiniGridRowBaseProps & { error: string | ReactNode; warning?: never };
export type MiniGridRowWithWarningProps = MiniGridRowBaseProps & { warning: string | ReactNode; error?: never };

/** Пропсы компонента MiniGridRow */
export type MiniGridRowProps = MiniGridRowWithoutStatusProps | MiniGridRowWithErrorProps | MiniGridRowWithWarningProps;

export const MiniGridRow = function MiniGridRow(props: MiniGridRowProps) {
    const {
        id,
        isDraggable = true,
        children,
        className,
        error,
        warning,
        title,
        selected,
        statusColor,
        withoutTopBorder,
        onClick,
        onHoverStart,
        onHoverEnd,
        onHoverChange,
        'data-testid': dataTestId = 'MiniGridRow',
    } = props;
    const { useSortable, DndCSS, draggable, dragIcon, dragIconPosition } = useContext(DraggableContext);
    const { isHeaderRow } = useContext(HeaderRowContext);
    const statusColorClass = useColorStatus({ color: statusColor, size: 'm' });

    const sortableProps = useSortable({
        id,
        disabled: !isDraggable,
    });

    const modifiedListeners = useMemo(
        () =>
            isDraggable && draggable
                ? {
                      ...sortableProps?.listeners,
                      onClick: (event: React.MouseEvent) => {
                          sortableProps?.listeners?.onClick?.(event);
                          // Без этого после drag and drop'а вызывается onClick
                          event.stopPropagation();
                      },
                      onMouseDown: (event: React.MouseEvent) => {
                          sortableProps?.listeners?.onMouseDown?.(event);
                          event.stopPropagation();
                      },
                      onPointerDown: (event: React.PointerEvent) => {
                          sortableProps?.listeners?.onPointerDown?.(event);
                          event.stopPropagation();
                      },
                  }
                : {},
        [sortableProps?.listeners, draggable, isDraggable],
    );

    const style = useMemo(() => {
        const transform = sortableProps?.transform ?? { x: 0, y: 0 };

        return isDraggable && draggable
            ? {
                  transform: DndCSS?.Transform.toString({ ...transform, scaleX: 1, scaleY: 1 }),
                  transition: sortableProps?.transition ?? 'none',
              }
            : {};
    }, [DndCSS?.Transform, sortableProps?.transition, sortableProps?.transform, draggable, isDraggable]);

    return (
        <TableRow
            title={title}
            onHoverEnd={onHoverEnd}
            onHoverStart={onHoverStart}
            onHoverChange={onHoverChange}
            withoutTopBorder={withoutTopBorder}
            hoverable={Boolean(onClick) || Boolean(onHoverStart) || Boolean(onHoverEnd) || Boolean(onHoverChange)}
            selected={selected}
            ref={sortableProps?.setNodeRef}
            className={cls(
                '',
                {
                    dragged: Boolean(sortableProps?.isDragging),
                },
                [className],
            )}
            data-testid={dataTestId}
            style={style}
            onClick={onClick}
            data-form-error={props['data-form-error']}
        >
            <TableCell className={cls('mainCell', [statusColorClass])}>
                {dragIconPosition === 'right' && children}
                {isDraggable &&
                    draggable &&
                    (isHeaderRow ? (
                        <EmptyCell />
                    ) : (
                        <Cell type="Icon" isGrowable={false}>
                            <button
                                {...sortableProps?.attributes}
                                {...modifiedListeners}
                                tabIndex={-1}
                                className={cls('dragButton')}
                                data-testid={`${dataTestId}.dragButton`}
                                style={clickableAria}
                                type="button"
                            >
                                {dragIcon}
                            </button>
                        </Cell>
                    ))}
                {dragIconPosition === 'left' && children}
                {error && <MiniGridRowStatusIcon kind="error" title={error} dataTestId={`${dataTestId}.error`} />}
                {warning && (
                    <MiniGridRowStatusIcon kind="warning" title={warning} dataTestId={`${dataTestId}.warning`} />
                )}
            </TableCell>
        </TableRow>
    );
};
